// customerTxnLookup.js
// ----------------------------------------------------------------------------
// Mobile-number lookup for the three transaction screens.
//
// Each screen's header already has a search box that takes a NUMBER — the pawn
// ticket on Payment, the loan/receipt ref on Receipt and Settlement. Tellers
// also get asked for a customer by phone ("I'm 98xxxxxxxx, what do I owe?"), so
// the same box now doubles as a customer search: type digits and the matching
// customers drop down under the box exactly like the Customer Details fields on
// the Payment screen; pick one and the screen loads THAT customer's most recent
// transaction on this counter —
//
//   Payment     ⇒ the last loan they took
//   Receipt     ⇒ the last repayment receipt they made
//   Settlement  ⇒ the last loan they closed out
//
// …and when the customer has nothing on this counter, the dropdown says so by
// name instead of loading a blank screen.
//
// The two behaviours coexist without ambiguity because the dropdown never
// hijacks the box: Enter still runs the screen's own voucher lookup, the ONE
// exception being an input that is exactly a 10-digit mobile number (no voucher
// is ever that long), where Enter takes the first suggestion.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  ClickAwayListener,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper as MuiPaper,
  Popper,
  Typography,
} from "@mui/material";
import {
  PersonRounded as PersonIcon,
  SearchOffRounded as SearchOffIcon,
} from "@mui/icons-material";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const BORDER = "#E2E8F0";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const WARN = "#D97706";
const WARN_BG = "#FFFBEB";

// Digits typed so far before the box is treated as (the start of) a mobile
// number. Short enough that the name appears while still typing, long enough
// that a 2–3 digit manual voucher number never pulls a dropdown.
const MIN_DIGITS = 4;
// A complete mobile number. Only at this exact length does Enter stop meaning
// "look up this voucher" and start meaning "take the first suggestion".
const FULL_MOBILE_DIGITS = 10;

const digitsOnly = (v) => /^\d+$/.test(String(v ?? "").trim());
const isMobileish = (v) => {
  const s = String(v ?? "").trim();
  return digitsOnly(s) && s.length >= MIN_DIGITS;
};

// What each screen calls its register, for the messages.
const KIND_NOUN = {
  loan: "loan",
  receipt: "receipt",
  settlement: "settled loan",
};

// ─── Hook: the search box's customer side ────────────────────────────────────
// storeId / pawnMode  — scope: the lookup only ever returns rows the ACTIVE
//                       book is allowed to open (official 1 + 3, unofficial
//                       2 + 3), enforced on the backend.
// kind                — "loan" | "receipt" | "settlement" (which register).
// onPick              — async (data, customer) => void. Handed the resolved
//                       transaction; the screen loads it however it likes.
// showSnackbar        — the screen's own snackbar, for hard failures.
export const useCustomerTxnLookup = ({
  storeId,
  pawnMode,
  kind,
  onPick,
  showSnackbar,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  // The customer picked who turned out to have nothing on this counter. Kept
  // in the dropdown (rather than flashed in a snackbar) so the teller can read
  // the name back and see they searched the right person.
  const [notFoundFor, setNotFoundFor] = useState(null);
  // The search box element, tracked by ref callback rather than captured on
  // focus — a detached anchor makes Popper fall back to the viewport corner.
  const [anchorEl, setAnchorEl] = useState(null);

  const debounceRef = useRef(null);
  // Monotonic request id: a slow response for "98" must not overwrite the
  // results for "9876".
  const reqRef = useRef(0);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const setAnchor = useCallback(
    (el) => setAnchorEl((prev) => (prev === el ? prev : el)),
    [],
  );

  const close = useCallback(() => setOpen(false), []);

  // Drop everything — used when the screen is cleared or the box emptied.
  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    reqRef.current += 1;
    setSuggestions([]);
    setOpen(false);
    setLoading(false);
    setNotFoundFor(null);
  }, []);

  const fetchSuggestions = useCallback(
    async (value) => {
      const myReq = (reqRef.current += 1);
      setLoading(true);
      setOpen(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/customers/store/${storeId}`,
          {
            params: {
              search: value,
              search_mode: "wild",
              sort: "ASC",
              limit: 10,
            },
          },
        );
        if (myReq !== reqRef.current) return;
        const rows = res.data?.success ? res.data.data || [] : [];
        setSuggestions(rows);
        setOpen(rows.length > 0);
      } catch {
        if (myReq !== reqRef.current) return;
        setSuggestions([]);
        setOpen(false);
      } finally {
        if (myReq === reqRef.current) setLoading(false);
      }
    },
    [storeId],
  );

  // Call from the search box's onChange, with the raw new value. Anything that
  // isn't digits (a voucher prefix, a "REC…" ref) simply closes the dropdown
  // and leaves the box to its normal job.
  const onSearchInput = useCallback(
    (value) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setNotFoundFor(null);
      const v = String(value ?? "").trim();
      if (storeId == null || !isMobileish(v)) {
        reqRef.current += 1;
        setSuggestions([]);
        setOpen(false);
        setLoading(false);
        return;
      }
      debounceRef.current = setTimeout(() => fetchSuggestions(v), 300);
    },
    [storeId, fetchSuggestions],
  );

  // Resolve the customer → their last transaction on this counter → hand it to
  // the screen. Returns nothing; failures surface in the dropdown / snackbar.
  const pick = useCallback(
    async (customer) => {
      const custId = customer?.customer_id;
      if (!custId || storeId == null) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setOpen(false);
      setNotFoundFor(null);
      setResolving(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/payments/customer-last-txn/${storeId}/${custId}`,
          { params: { mode: pawnMode, kind } },
        );
        if (res.data?.success && res.data.data) {
          await onPick?.(res.data.data, customer);
          setSuggestions([]);
        } else {
          // Nothing on this counter for them — say who, by name.
          setNotFoundFor(customer.name || String(custId));
          setOpen(true);
        }
      } catch (err) {
        showSnackbar?.(
          err.response?.data?.statusMessage ||
            "Failed to look up this customer's last transaction",
          "error",
        );
      } finally {
        setResolving(false);
      }
    },
    [storeId, pawnMode, kind, onPick, showSnackbar],
  );

  // Wire into the search box's onKeyDown BEFORE the screen's own Enter
  // handling. Returns true when the keystroke was consumed here, so the caller
  // skips its voucher lookup.
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        if (open) {
          setOpen(false);
          return true;
        }
        return false;
      }
      if (e.key !== "Enter") return false;
      // A full 10-digit number can only be a mobile; anything shorter stays a
      // voucher lookup so numeric manual vouchers keep working.
      const v = String(e.target.value ?? "").trim();
      if (
        open &&
        suggestions.length > 0 &&
        digitsOnly(v) &&
        v.length === FULL_MOBILE_DIGITS
      ) {
        e.preventDefault();
        pick(suggestions[0]);
        return true;
      }
      setOpen(false);
      return false;
    },
    [open, suggestions, pick],
  );

  return {
    // Spread onto the search TextField: `ref={lookup.setAnchor}`.
    setAnchor,
    onSearchInput,
    handleKeyDown,
    reset,
    close,
    resolving,
    // Spread onto <CustomerTxnPopper {...lookup.popperProps} />.
    popperProps: {
      open,
      anchorEl,
      customers: suggestions,
      loading,
      notFoundFor,
      kind,
      onSelect: pick,
      onClose: close,
    },
  };
};

// ─── The dropdown ────────────────────────────────────────────────────────────
// Three states in one popper: the matching customers, a spinner while they are
// being fetched, and the by-name "no record found" once one is picked who has
// nothing here. `accent` lets the settlement screen tint it green like the rest
// of that sheet (it is passed a CSS var, so MUI colour maths is never applied).
export const CustomerTxnPopper = ({
  open,
  anchorEl,
  customers,
  loading,
  notFoundFor,
  kind,
  onSelect,
  onClose,
  accent = "#2563EB",
  accentLight = "#DBEAFE",
  accentPale = "#EFF6FF",
}) => {
  // A missing or detached anchor gives Popper a zero-sized rect, which drops
  // the dropdown into the top-left corner of the viewport.
  const anchored = !!anchorEl && anchorEl.isConnected !== false;
  if (!anchored) return null;

  const hasBody = loading || !!notFoundFor || (customers?.length || 0) > 0;

  return (
    <Popper
      open={!!open && hasBody}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{
        width: Math.max(anchorEl.offsetWidth || 0, 260),
        zIndex: 1300,
      }}
      modifiers={[
        { name: "flip", enabled: true },
        { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
      ]}
    >
      <ClickAwayListener
        onClickAway={(e) => {
          // Clicks on the search box itself must not close the dropdown —
          // the teller is still typing into it.
          if (anchorEl && e?.target && anchorEl.contains(e.target)) return;
          onClose?.();
        }}
      >
        <MuiPaper
          sx={{
            maxHeight: { xs: "45vh", sm: 320 },
            overflow: "auto",
            mt: 0.5,
            boxShadow: "0 8px 32px rgba(15,23,42,0.15)",
            borderRadius: "10px",
            border: `1px solid ${BORDER}`,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} sx={{ color: accent }} />
            </Box>
          ) : notFoundFor ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                p: 1.5,
                bgcolor: WARN_BG,
              }}
            >
              <SearchOffIcon sx={{ fontSize: 20, color: WARN, mt: "1px" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: WARN }}
                >
                  No record found for {notFoundFor} customer
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: INK_MUTED, display: "block" }}
                >
                  Nothing on this counter yet — no {KIND_NOUN[kind] || "record"}{" "}
                  in the current book.
                </Typography>
              </Box>
            </Box>
          ) : (
            <List dense disablePadding>
              {customers?.map((cust) => (
                <ListItem
                  key={cust.customer_id}
                  button
                  onClick={() => onSelect?.(cust)}
                  sx={{
                    "&:hover": { backgroundColor: accentPale },
                    borderBottom: `1px solid ${BORDER}`,
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      sx={{
                        bgcolor: accentLight,
                        width: 30,
                        height: 30,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 18, color: accent }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: INK }}
                      >
                        {cust.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{ color: INK_MUTED, display: "block" }}
                      >
                        📱 {cust.mobile}
                        {cust.relative_name
                          ? ` · ${
                              cust.relative_relation
                                ? `${cust.relative_relation} `
                                : ""
                            }${cust.relative_name}`
                          : ""}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </MuiPaper>
      </ClickAwayListener>
    </Popper>
  );
};

export default useCustomerTxnLookup;
